import { useCallback, useMemo } from "react";
import { Link, useParams } from "react-router";
import { styled } from "styled-components";

import { bookmarksApiSlice } from "api/bookmarksApi.slice";
import { MasterDetailLayout } from "common/components";
import { useTranslate } from "common/utilities/useTranslate";
import { BookmarkList } from "domains/bookmarks/components/BookmarksList";
import { ClusterDetail } from "domains/clusters/components/ClusterDetail";
import { InteractiveDataDetailSection } from "domains/knowledgeData/components/InteractiveDataDetailSection";
import { interpolate } from "domains/language/interpolate";
import { PublicationDetailSection } from "domains/publication/components/PublicationDetailSection";
import { VideoDetailSection } from "domains/videos/components/VideoDetailSection";
import { KnowledgeType } from "entities/knowledge/types";

const StyledContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundPrimary};
  height: 100%;
  color: ${({ theme }) => theme.colors.fillPrimary};
  overflow: hidden;
`;

const StyledLink = styled(Link)`
  transition: color 0.3s;
  text-decoration: none;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.blue};

  &:hover {
    color: ${({ theme }) => theme.colors.blueDark};
  }
`;

export const BookmarksPage = () => {
  const { bookmarkId } = useParams();
  const translate = useTranslate();

  const {
    data: paginatedBookmarks,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = bookmarksApiSlice.endpoints.getBookmarks.useInfiniteQuery(undefined);

  const bookmarks = useMemo(() => paginatedBookmarks?.pages.flat() ?? [], [paginatedBookmarks?.pages]);

  const bookmark = useMemo(() => {
    const selectedBookmark = bookmarks.find((bookmark) => bookmark.id === bookmarkId);

    if (selectedBookmark) return selectedBookmark;

    return bookmarks[0];
  }, [bookmarkId, bookmarks]);

  const loadNextPage = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) await fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const list =
    bookmarks.length === 0 && !isFetching ? (
      <p>
        {interpolate(translate("app.bookmarks.empty_message"), {
          monitor: (value) => <StyledLink to="/monitor">{value}</StyledLink>,
        })}
      </p>
    ) : (
      <BookmarkList
        items={bookmarks}
        hasNextPage={hasNextPage}
        isPending={isFetching}
        selectedId={bookmark?.id}
        loadNextPage={loadNextPage}
      />
    );

  const getLink = (knowledgeId: string) => `/search/publications/${knowledgeId}`;

  const shareLink = `${import.meta.env.VITE_WEBSITE_URL}${getLink(bookmark?.id ?? "")}`;

  const renderKnowledgeDetailPage = () => {
    if (!bookmark) return;

    if (bookmark?.type === "cluster") return;

    if (bookmark?.embedded_media && bookmark.attachment?.type === "video")
      return (
        <VideoDetailSection
          id={bookmark.id}
          shareLink={shareLink}
          topicBack={{ term: "app.discover.topic.tabs.videos.title" }}
        />
      );

    if (bookmark?.interactive_link)
      return (
        <InteractiveDataDetailSection
          id={bookmark.id}
          shareLink={shareLink}
          topicBack={{ term: "app.discover.topic.tabs.interactive_data.title" }}
        />
      );

    return (
      <PublicationDetailSection
        id={bookmark.id}
        shareLink={shareLink}
        topicBack={{ term: "app.discover.topic.tabs.knowledge.title" }}
      />
    );
  };

  return (
    <StyledContainer>
      <MasterDetailLayout
        listTitle={translate("app.bookmarks.title")}
        listComponent={list}
        mainView={bookmarkId ? "right" : "left"}
        listBackNavigation={{
          label: translate("app.navigation.back"),
          path: "/",
        }}
        detailBackNavigation={{
          label: translate("app.bookmarks.title"),
          path: "/bookmarks",
        }}
      >
        <>
          {bookmark?.type === "cluster" && <ClusterDetail cluster={bookmark} id={bookmark.id} topicBack={topicBack} />}
          {bookmark?.type === KnowledgeType.article && renderKnowledgeDetailPage()}
        </>
      </MasterDetailLayout>
    </StyledContainer>
  );
};

const topicBack = { term: "app.bookmarks.title" } as const;
